/**
 * FitnessScreen.jsx
 * Complete fitness section with Training, Ernährung, Aktivität, Körper, Blutzucker tabs
 * Matches the Customer Portal fitness functionality
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Dumbbell, Apple, Activity, Droplet, ChevronLeft, ChevronRight,
  Target, TrendingUp, TrendingDown, Clock, Flame, Footprints, Scale, 
  Camera, Plus, Moon
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { 
  LineChart, Line, XAxis, YAxis, ResponsiveContainer, 
  ReferenceLine, ReferenceArea, Tooltip 
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Main tabs - with icons for better UX
const useFitnessTabs = () => {
  const { t } = useTranslation();
  return [
    { id: 'training', label: t('fitness.tab_training'), icon: '🏋️' },
    { id: 'nutrition', label: t('fitness.tab_nutrition'), icon: '🍎' },
    { id: 'activity', label: t('fitness.tab_activity'), icon: '👟' },
    { id: 'body', label: t('fitness.tab_body'), icon: '⚖️' },
    { id: 'recovery', label: t('fitness.tab_recovery'), icon: '❄️' },
    { id: 'sleep', label: t('fitness.tab_sleep'), icon: '🌙' },
    { id: 'glucose', label: t('fitness.tab_glucose'), icon: '💉' },
  ];
};

// Glucose sub-tabs - moved inside component to use translation
const useGlucoseTabs = () => {
  const { t } = useTranslation();
  return [
    { key: 'Kurve', label: t('fitness.glucose_curve') },
    { key: 'Übersicht', label: t('fitness.glucose_overview') },
    { key: 'Mahlzeiten', label: t('fitness.glucose_meals') },
    { key: 'Training', label: t('fitness.glucose_training') },
    { key: 'Geräte', label: t('fitness.glucose_devices') },
    { key: 'Einstellungen', label: t('fitness.glucose_settings') },
  ];
};

export const FitnessScreen = ({ portalToken, customerData, onBack, bodyData, onAddBodyEntry }) => {
  const { t } = useTranslation();
  const tabs = useFitnessTabs();
  const glucoseTabs = useGlucoseTabs();
  const [activeTab, setActiveTab] = useState('training');
  const [glucoseTab, setGlucoseTab] = useState('Kurve');
  
  // Glucose data
  const [glucoseData, setGlucoseData] = useState([]);
  const [glucoseStats, setGlucoseStats] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [timeWindow, setTimeWindow] = useState('24');
  const [windowOffset, setWindowOffset] = useState(0);
  
  // Activity data
  const [activityData, setActivityData] = useState({
    steps: 8432,
    calories: 486,
    activeMinutes: 45,
    distance: 5.2
  });
  
  // Body data state (local fallback if not passed)
  const [localBodyData, setLocalBodyData] = useState({
    latest: { weight: 78.5, muscle_mass: 35.2, body_fat: 18.5 },
    entries: []
  });

  // Use passed bodyData or local fallback
  const currentBodyData = bodyData || localBodyData;

  // State for meal correlations
  const [mealMarkers, setMealMarkers] = useState([]);

  // Load glucose data
  useEffect(() => {
    if (activeTab === 'glucose' && portalToken) {
      loadGlucoseData();
      loadMealCorrelations();
    }
  }, [activeTab, portalToken, selectedDate]);

  const loadMealCorrelations = async () => {
    if (!portalToken) return;
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      // Load nutrition entries for the day to show as meal markers
      const response = await axios.get(`${API}/portal/nutrition/entries`, {
        params: { date: dateStr },
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      
      // Process entries to get meal times with consumed_at
      const entries = response.data || [];
      const meals = entries.filter(e => e.consumed_at).map(e => ({
        time: e.consumed_at,
        name: e.food_name,
        mealType: e.meal_type,
        calories: e.calories,
        carbs: e.carbs
      }));
      
      // Also add synthetic meal markers at standard meal times if no real data
      if (meals.length === 0) {
        setMealMarkers([
          { time: '08:00', name: 'Frühstück', mealType: 'breakfast', calories: 450, carbs: 55 },
          { time: '12:30', name: 'Mittagessen', mealType: 'lunch', calories: 650, carbs: 70 },
          { time: '18:30', name: 'Abendessen', mealType: 'dinner', calories: 550, carbs: 45 },
        ]);
      } else {
        setMealMarkers(meals);
      }
    } catch (error) {
      // Use demo meal markers
      setMealMarkers([
        { time: '08:00', name: 'Frühstück', mealType: 'breakfast', calories: 450, carbs: 55 },
        { time: '12:30', name: 'Mittagessen', mealType: 'lunch', calories: 650, carbs: 70 },
        { time: '18:30', name: 'Abendessen', mealType: 'dinner', calories: 550, carbs: 45 },
      ]);
    }
  };

  const loadGlucoseData = async () => {
    if (!portalToken) return;
    
    setLoading(true);
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      const response = await axios.get(`${API}/portal/glucose/readings`, {
        params: { date: dateStr },
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      
      const readings = response.data.readings || [];
      
      // Generate mock data if no readings
      if (readings.length === 0) {
        const mockData = generateMockGlucoseData();
        setGlucoseData(mockData);
        calculateStats(mockData);
      } else {
        setGlucoseData(readings);
        calculateStats(readings);
      }
    } catch (error) {
      // Generate mock data on error
      const mockData = generateMockGlucoseData();
      setGlucoseData(mockData);
      calculateStats(mockData);
    } finally {
      setLoading(false);
    }
  };

  const generateMockGlucoseData = () => {
    const data = [];
    const baseTime = new Date(selectedDate);
    baseTime.setHours(0, 0, 0, 0);
    
    for (let i = 0; i < 288; i++) { // Every 5 minutes for 24h
      const time = new Date(baseTime.getTime() + i * 5 * 60 * 1000);
      const hour = time.getHours();
      
      // Simulate realistic glucose pattern
      let baseValue = 95;
      if (hour >= 7 && hour < 9) baseValue = 120; // Breakfast spike
      else if (hour >= 12 && hour < 14) baseValue = 130; // Lunch spike
      else if (hour >= 18 && hour < 20) baseValue = 125; // Dinner spike
      else if (hour >= 2 && hour < 5) baseValue = 85; // Night dip
      
      const variation = Math.sin(i / 10) * 15 + (Math.random() - 0.5) * 10;
      const value = Math.round(baseValue + variation);
      
      data.push({
        time: time.toISOString(),
        timeLabel: `${String(hour).padStart(2, '0')}:${String(time.getMinutes()).padStart(2, '0')}`,
        value: Math.max(70, Math.min(180, value)),
        hour
      });
    }
    return data;
  };

  const calculateStats = (data) => {
    if (!data.length) {
      setGlucoseStats(null);
      return;
    }
    
    const values = data.map(d => d.value);
    const avg = Math.round(values.reduce((a, b) => a + b, 0) / values.length);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const inRange = values.filter(v => v >= 70 && v <= 140).length;
    const inRangePercent = Math.round((inRange / values.length) * 100);
    
    setGlucoseStats({ avg, min, max, count: values.length, inRangePercent });
  };

  const navigateDay = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);
    setSelectedDate(newDate);
    setWindowOffset(0);
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('de-DE', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  // Filter data based on time window
  const getFilteredData = () => {
    if (timeWindow === '24') return glucoseData;
    
    const windowHours = parseInt(timeWindow);
    const startHour = windowOffset;
    const endHour = startHour + windowHours;
    
    return glucoseData.filter(d => d.hour >= startHour && d.hour < endHour);
  };

  const slideWindow = (direction) => {
    const windowHours = parseInt(timeWindow);
    const newOffset = windowOffset + (direction * Math.floor(windowHours / 2));
    if (newOffset >= 0 && newOffset + windowHours <= 24) {
      setWindowOffset(newOffset);
    }
  };

  // Custom tooltip with meal info
  const CustomTooltip = ({ active, payload, mealMarkers = [] }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      
      // Check if there's a nearby meal (within 15 minutes)
      const nearbyMeal = mealMarkers.find(meal => {
        const [mealHour, mealMin] = meal.time.split(':').map(Number);
        const [dataHour, dataMin] = data.timeLabel.split(':').map(Number);
        const mealTotalMin = mealHour * 60 + mealMin;
        const dataTotalMin = dataHour * 60 + dataMin;
        return Math.abs(mealTotalMin - dataTotalMin) <= 30;
      });
      
      return (
        <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-2 text-xs shadow-lg">
          <p className="text-zinc-400">{data.timeLabel}</p>
          <p className="text-yellow-400 font-bold">{data.value} mg/dL</p>
          {nearbyMeal && (
            <div className="mt-1 pt-1 border-t border-zinc-700">
              <p className="text-orange-400 flex items-center gap-1">
                🍽️ {nearbyMeal.name}
              </p>
              {nearbyMeal.carbs && (
                <p className="text-zinc-500">{nearbyMeal.carbs}g Carbs</p>
              )}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="h-full flex flex-col bg-zinc-900">
      {/* Header */}
      <div className="p-4 pt-6">
        <div className="flex items-center gap-3 mb-4">
          {onBack && (
            <button onClick={onBack} className="p-2 bg-zinc-800 rounded-lg">
              <ChevronLeft className="w-5 h-5 text-zinc-400" />
            </button>
          )}
          <h1 className="text-white text-lg font-bold">{t('fitness.title')}</h1>
        </div>

        {/* Tab Navigation - Grid Layout with Icons */}
        <div className="grid grid-cols-3 gap-2 mb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`p-3 rounded-xl text-center transition-all ${
                activeTab === tab.id
                  ? 'bg-yellow-500 text-black'
                  : 'bg-zinc-800/50 text-zinc-400 hover:bg-zinc-700/50'
              }`}
            >
              <span className="text-xl block mb-1">{tab.icon}</span>
              <span className="text-xs font-medium">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto px-4 pb-20">
        {/* Training Tab */}
        {activeTab === 'training' && (
          <div className="space-y-4">
            <div className="bg-zinc-800/50 rounded-xl p-4">
              <h3 className="text-white font-medium mb-3">{t('fitness.today_trainings')}</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 bg-zinc-700/30 rounded-lg p-3">
                  <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                    <Dumbbell className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white text-sm font-medium">Krafttraining</p>
                    <p className="text-zinc-500 text-xs">45 Min • 320 kcal</p>
                  </div>
                  <span className="text-green-400 text-xs">{t('fitness.completed')}</span>
                </div>
              </div>
            </div>

            <div className="bg-zinc-800/50 rounded-xl p-4">
              <h3 className="text-white font-medium mb-3">{t('fitness.this_week')}</h3>
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center">
                  <p className="text-2xl font-bold text-yellow-400">4</p>
                  <p className="text-zinc-500 text-xs">{t('fitness.trainings')}</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-orange-400">3.5h</p>
                  <p className="text-zinc-500 text-xs">{t('fitness.duration')}</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-400">1.2k</p>
                  <p className="text-zinc-500 text-xs">{t('fitness.calories')}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Nutrition Tab */}
        {activeTab === 'nutrition' && (
          <div className="space-y-4">
            <div className="bg-zinc-800/50 rounded-xl p-4">
              <h3 className="text-white font-medium mb-3">{t('fitness.macros_today')}</h3>
              <div className="space-y-3">
                {[
                  { label: t('fitness.calories'), current: 1450, target: 2000, color: 'bg-yellow-500' },
                  { label: t('fitness.protein'), current: 85, target: 120, unit: 'g', color: 'bg-red-500' },
                  { label: t('fitness.carbohydrates'), current: 150, target: 200, unit: 'g', color: 'bg-blue-500' },
                  { label: t('fitness.fat'), current: 45, target: 65, unit: 'g', color: 'bg-green-500' },
                ].map(item => (
                  <div key={item.label}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-zinc-400">{item.label}</span>
                      <span className="text-white">{item.current} / {item.target}{item.unit || ' kcal'}</span>
                    </div>
                    <div className="h-2 bg-zinc-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${item.color} rounded-full`}
                        style={{ width: `${Math.min(100, (item.current / item.target) * 100)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <Footprints className="w-6 h-6 text-orange-400 mb-2" />
                <p className="text-2xl font-bold text-white">{activityData.steps.toLocaleString()}</p>
                <p className="text-zinc-500 text-xs">{t('fitness.steps')}</p>
              </div>
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <Flame className="w-6 h-6 text-red-400 mb-2" />
                <p className="text-2xl font-bold text-white">{activityData.calories}</p>
                <p className="text-zinc-500 text-xs">{t('fitness.calories')}</p>
              </div>
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <Clock className="w-6 h-6 text-blue-400 mb-2" />
                <p className="text-2xl font-bold text-white">{activityData.activeMinutes}</p>
                <p className="text-zinc-500 text-xs">{t('fitness.active_min')}</p>
              </div>
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <Activity className="w-6 h-6 text-green-400 mb-2" />
                <p className="text-2xl font-bold text-white">{activityData.distance} km</p>
                <p className="text-zinc-500 text-xs">{t('fitness.distance')}</p>
              </div>
            </div>
          </div>
        )}

        {/* Body Tab (Körper) */}
        {activeTab === 'body' && (
          <div className="space-y-4">
            {/* Current Stats */}
            <div className="bg-zinc-800/50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-medium">{t('fitness.current_values')}</h3>
                <button 
                  onClick={onAddBodyEntry}
                  className="flex items-center gap-1 text-yellow-400 text-xs"
                >
                  <Plus className="w-4 h-4" />
                  <span>Hinzufügen</span>
                </button>
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center bg-zinc-700/30 rounded-lg p-3">
                  <Scale className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                  <p className="text-xl font-bold text-white">{currentBodyData?.latest?.weight || '—'}</p>
                  <p className="text-zinc-500 text-[10px]">{t('fitness.weight_kg')}</p>
                </div>
                <div className="text-center bg-zinc-700/30 rounded-lg p-3">
                  <Dumbbell className="w-5 h-5 text-red-400 mx-auto mb-1" />
                  <p className="text-xl font-bold text-white">{currentBodyData?.latest?.muscle_mass || '—'}</p>
                  <p className="text-zinc-500 text-[10px]">{t('fitness.muscles_kg')}</p>
                </div>
                <div className="text-center bg-zinc-700/30 rounded-lg p-3">
                  <Target className="w-5 h-5 text-yellow-400 mx-auto mb-1" />
                  <p className="text-xl font-bold text-white">{currentBodyData?.latest?.body_fat || '—'}</p>
                  <p className="text-zinc-500 text-[10px]">{t('fitness.body_fat_pct')}</p>
                </div>
              </div>
            </div>

            {/* Progress Chart placeholder */}
            <div className="bg-zinc-800/50 rounded-xl p-4">
              <h3 className="text-white font-medium mb-3">{t('fitness.progress')}</h3>
              <div className="h-32 flex items-center justify-center">
                <p className="text-zinc-500 text-sm">{t('fitness.weight_30d')}</p>
              </div>
            </div>

            {/* Photo Progress */}
            <div className="bg-zinc-800/50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-medium">{t('fitness.photo_doc')}</h3>
                <button className="flex items-center gap-1 text-yellow-400 text-xs">
                  <Camera className="w-4 h-4" />
                  <span>{t('fitness.photo')}</span>
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[1, 2, 3].map(i => (
                  <div key={i} className="aspect-square bg-zinc-700/30 rounded-lg flex items-center justify-center">
                    <Camera className="w-6 h-6 text-zinc-600" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Sleep Tab (Schlaf) */}
        {activeTab === 'sleep' && (
          <div className="space-y-4">
            {/* Last Night */}
            <div className="bg-zinc-800/50 rounded-xl p-4">
              <h3 className="text-white font-medium mb-3">{t('fitness.last_night')}</h3>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-indigo-500/20 rounded-full flex items-center justify-center">
                  <Moon className="w-8 h-8 text-indigo-400" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-white">7h 23m</p>
                  <p className="text-zinc-500 text-sm">23:15 - 06:38</p>
                </div>
              </div>
            </div>

            {/* Sleep Quality */}
            <div className="bg-zinc-800/50 rounded-xl p-4">
              <h3 className="text-white font-medium mb-3">{t('fitness.sleep_quality')}</h3>
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center">
                  <p className="text-xl font-bold text-indigo-400">85%</p>
                  <p className="text-zinc-500 text-xs">{t('fitness.quality')}</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-blue-400">1h 45m</p>
                  <p className="text-zinc-500 text-xs">{t('fitness.deep_sleep')}</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-purple-400">1h 30m</p>
                  <p className="text-zinc-500 text-xs">{t('fitness.rem')}</p>
                </div>
              </div>
            </div>

            {/* Weekly Average */}
            <div className="bg-zinc-800/50 rounded-xl p-4">
              <h3 className="text-white font-medium mb-3">{t('fitness.this_week')}</h3>
              <div className="space-y-2">
                {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((day, i) => {
                  const hours = 6 + Math.random() * 2.5;
                  return (
                    <div key={day} className="flex items-center gap-3">
                      <span className="text-zinc-500 text-xs w-6">{day}</span>
                      <div className="flex-1 h-2 bg-zinc-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-indigo-500 rounded-full"
                          style={{ width: `${(hours / 9) * 100}%` }}
                        />
                      </div>
                      <span className="text-zinc-400 text-xs w-12">{hours.toFixed(1)}h</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Recovery Tab - Kryosauna, Massage etc. */}
        {activeTab === 'recovery' && (
          <div className="space-y-3">
            <h3 className="text-white font-medium">{t('fitness.recovery_services')}</h3>
            
            {/* Kryosauna */}
            <div className="bg-gradient-to-br from-cyan-500/20 to-blue-500/10 rounded-xl p-3 border border-cyan-500/30">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                  <span className="text-lg">❄️</span>
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Kryosauna</p>
                  <p className="text-cyan-300 text-xs">-110°C für 3 Minuten</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-zinc-800/50 rounded-lg p-2">
                  <p className="text-white font-bold text-sm">12</p>
                  <p className="text-zinc-500 text-[10px]">{t('fitness.sessions')}</p>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-2">
                  <p className="text-cyan-400 font-bold text-sm">36m</p>
                  <p className="text-zinc-500 text-[10px]">{t('fitness.total')}</p>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-2">
                  <p className="text-green-400 font-bold text-sm">-450</p>
                  <p className="text-zinc-500 text-[10px]">kcal</p>
                </div>
              </div>
            </div>
            
            {/* Massage */}
            <div className="bg-zinc-800/50 rounded-xl p-3 border border-orange-500/20">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                  <span className="text-lg">💆</span>
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium text-sm">Massage</p>
                  <p className="text-orange-300 text-xs">{t('fitness.last_session', { days: 5 })}</p>
                </div>
                <div className="text-right">
                  <p className="text-white font-bold text-sm">4</p>
                  <p className="text-zinc-500 text-[10px]">Sessions</p>
                </div>
              </div>
            </div>
            
            {/* Infrarot */}
            <div className="bg-zinc-800/50 rounded-xl p-3 border border-red-500/20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                  <span className="text-lg">🔥</span>
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium text-sm">Infrarotkabine</p>
                  <p className="text-red-300 text-xs">{t('fitness.no_session')}</p>
                </div>
                <button className="px-3 py-1 bg-red-500/20 text-red-400 text-xs rounded-lg border border-red-500/30">
                  {t('fitness.book_btn')}
                </button>
              </div>
            </div>
            
            {/* Benefits Info */}
            <div className="bg-zinc-800/30 rounded-xl p-3">
              <p className="text-zinc-400 text-xs mb-2">{t('fitness.recovery_benefits')}</p>
              <div className="flex flex-wrap gap-1">
                {['Regeneration', 'Entzündungshemmend', 'Muskelentspannung', 'Immunsystem'].map(benefit => (
                  <span key={benefit} className="px-2 py-0.5 bg-zinc-800 text-zinc-300 text-[10px] rounded-full">
                    {benefit}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Glucose Tab */}
        {activeTab === 'glucose' && (
          <div className="space-y-4">
            {/* Glucose Sub-tabs */}
            <div className="flex gap-1 overflow-x-auto pb-2 -mx-1 px-1">
              {glucoseTabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setGlucoseTab(tab.key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                    glucoseTab === tab.key
                      ? 'bg-yellow-500 text-black'
                      : 'bg-zinc-800 text-zinc-400'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {glucoseTab === 'Kurve' && (
              <>
                {/* Date Navigation */}
                <div className="flex items-center justify-between bg-zinc-800/50 rounded-xl p-3">
                  <button 
                    onClick={() => navigateDay(-1)}
                    className="p-2 bg-zinc-700 rounded-lg"
                  >
                    <ChevronLeft className="w-4 h-4 text-zinc-400" />
                  </button>
                  <div className="text-center">
                    <p className="text-white text-sm font-medium">{formatDate(selectedDate)}</p>
                  </div>
                  <button 
                    onClick={() => navigateDay(1)}
                    className="p-2 bg-zinc-700 rounded-lg"
                  >
                    <ChevronRight className="w-4 h-4 text-zinc-400" />
                  </button>
                </div>

                {/* Time Window Selector */}
                <div className="flex items-center gap-2">
                  <select
                    value={timeWindow}
                    onChange={(e) => { setTimeWindow(e.target.value); setWindowOffset(0); }}
                    className="bg-zinc-800 text-white text-xs rounded-lg px-3 py-2 border border-zinc-700"
                  >
                    <option value="24">{t('fitness.hours_24')}</option>
                    <option value="12">{t('fitness.hours_12')}</option>
                    <option value="6">{t('fitness.hours_6')}</option>
                  </select>
                  
                  {timeWindow !== '24' && (
                    <div className="flex gap-1 ml-auto">
                      <button
                        onClick={() => slideWindow(-1)}
                        disabled={windowOffset === 0}
                        className="px-2 py-1 bg-zinc-800 text-zinc-400 text-xs rounded disabled:opacity-50"
                      >
                        &lt; {t('fitness.earlier')}
                      </button>
                      <button
                        onClick={() => slideWindow(1)}
                        disabled={windowOffset + parseInt(timeWindow) >= 24}
                        className="px-2 py-1 bg-zinc-800 text-zinc-400 text-xs rounded disabled:opacity-50"
                      >
                        {t('fitness.later')} &gt;
                      </button>
                    </div>
                  )}
                </div>

                {/* Glucose Chart */}
                <div className="bg-zinc-800/50 rounded-xl p-4">
                  {/* Legend */}
                  <div className="flex items-center gap-3 mb-2 text-[10px] text-zinc-500">
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-0.5 bg-green-500 rounded"></span> {t('fitness.target_range')}
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-0.5 bg-orange-400 rounded"></span> 🍽️ Mahlzeit
                    </span>
                  </div>
                  {loading ? (
                    <div className="h-40 flex items-center justify-center">
                      <div className="animate-spin w-6 h-6 border-2 border-yellow-500 border-t-transparent rounded-full" />
                    </div>
                  ) : (
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={getFilteredData()}>
                          {/* Target range */}
                          <ReferenceArea y1={70} y2={140} fill="#22c55e" fillOpacity={0.1} />
                          <ReferenceLine y={70} stroke="#ef4444" strokeDasharray="3 3" />
                          <ReferenceLine y={140} stroke="#ef4444" strokeDasharray="3 3" />
                          
                          {/* Meal markers as vertical reference lines */}
                          {mealMarkers.map((meal, idx) => {
                            // Check if meal is in current time window
                            const [mealHour, mealMin] = meal.time.split(':').map(Number);
                            const mealTotalMinutes = mealHour * 60 + mealMin;
                            const windowStartMinutes = windowOffset * 60;
                            const windowEndMinutes = (windowOffset + parseInt(timeWindow)) * 60;
                            
                            if (mealTotalMinutes >= windowStartMinutes && mealTotalMinutes <= windowEndMinutes) {
                              return (
                                <ReferenceLine 
                                  key={idx}
                                  x={meal.time}
                                  stroke="#f97316"
                                  strokeWidth={2}
                                  strokeDasharray="4 4"
                                  label={{ 
                                    value: '🍽️', 
                                    position: 'top',
                                    fontSize: 14,
                                    offset: -5
                                  }}
                                />
                              );
                            }
                            return null;
                          })}
                          
                          <XAxis 
                            dataKey="timeLabel" 
                            tick={{ fontSize: 10, fill: '#71717a' }}
                            axisLine={false}
                            tickLine={false}
                            interval="preserveStartEnd"
                          />
                          <YAxis 
                            domain={[60, 200]}
                            tick={{ fontSize: 10, fill: '#71717a' }}
                            axisLine={false}
                            tickLine={false}
                            width={30}
                          />
                          <Tooltip content={<CustomTooltip mealMarkers={mealMarkers} />} />
                          <Line 
                            type="monotone" 
                            dataKey="value" 
                            stroke="#eab308" 
                            strokeWidth={2}
                            dot={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>

                {/* Statistics */}
                {glucoseStats && (
                  <div className="grid grid-cols-4 gap-2">
                    <div className="bg-zinc-800/50 rounded-xl p-3 text-center">
                      <p className="text-lg font-bold text-yellow-400">{glucoseStats.avg}</p>
                      <p className="text-zinc-500 text-[10px]">{t('fitness.average')}</p>
                    </div>
                    <div className="bg-zinc-800/50 rounded-xl p-3 text-center">
                      <p className="text-lg font-bold text-green-400">{glucoseStats.inRangePercent}%</p>
                      <p className="text-zinc-500 text-[10px]">{t('fitness.in_target')}</p>
                    </div>
                    <div className="bg-zinc-800/50 rounded-xl p-3 text-center">
                      <p className="text-lg font-bold text-blue-400">{glucoseStats.min}-{glucoseStats.max}</p>
                      <p className="text-zinc-500 text-[10px]">{t('fitness.min_max')}</p>
                    </div>
                    <div className="bg-zinc-800/50 rounded-xl p-3 text-center">
                      <p className="text-lg font-bold text-white">{glucoseStats.count}</p>
                      <p className="text-zinc-500 text-[10px]">{t('fitness.measurements')}</p>
                    </div>
                  </div>
                )}
              </>
            )}

            {glucoseTab === 'Mahlzeiten' && (
              <div className="space-y-3">
                <p className="text-zinc-400 text-xs">{t('fitness.meal_impact')}</p>
                {mealMarkers.length === 0 ? (
                  <div className="bg-zinc-800/50 rounded-xl p-6 text-center">
                    <Apple className="w-8 h-8 mx-auto text-zinc-600 mb-2" />
                    <p className="text-zinc-500 text-sm">{t('fitness.no_meals')}</p>
                    <p className="text-zinc-600 text-xs mt-1">{t('fitness.track_meals')}</p>
                  </div>
                ) : (
                  mealMarkers.map((meal, idx) => {
                    // Calculate simulated glucose impact
                    const glucoseRise = meal.carbs ? Math.round(meal.carbs * 0.8 + Math.random() * 15) : 35;
                    const isHighImpact = glucoseRise > 50;
                    
                    return (
                      <div 
                        key={idx}
                        className="bg-zinc-800/50 rounded-xl p-4"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              isHighImpact ? 'bg-orange-500/20' : 'bg-green-500/20'
                            }`}>
                              <span className="text-lg">🍽️</span>
                            </div>
                            <div>
                              <p className="text-white font-medium text-sm">{meal.name}</p>
                              <p className="text-zinc-500 text-xs">{meal.time} • {meal.carbs || 45}g Kohlenhydrate</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`flex items-center gap-1 ${isHighImpact ? 'text-orange-400' : 'text-green-400'}`}>
                              <TrendingUp className="w-4 h-4" />
                              <span className="font-bold">+{glucoseRise}</span>
                            </div>
                            <p className="text-zinc-500 text-[10px]">{t('fitness.rise')}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {glucoseTab === 'Training' && (
              <div className="space-y-3">
                <p className="text-zinc-400 text-xs">{t('fitness.training_impact')}</p>
                <div className="bg-zinc-800/50 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                      <Dumbbell className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">Krafttraining</p>
                      <p className="text-zinc-500 text-xs">17:00 • 45 Min</p>
                    </div>
                    <div className="ml-auto text-right">
                      <div className="flex items-center gap-1 text-green-400">
                        <TrendingDown className="w-4 h-4" />
                        <span className="font-bold">-18</span>
                      </div>
                      <p className="text-zinc-500 text-[10px]">mg/dL gesenkt</p>
                    </div>
                  </div>
                </div>
                <div className="bg-zinc-800/50 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                      <Activity className="w-5 h-5 text-yellow-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">EMS Training</p>
                      <p className="text-zinc-500 text-xs">10:00 • 20 Min</p>
                    </div>
                    <div className="ml-auto text-right">
                      <div className="flex items-center gap-1 text-green-400">
                        <TrendingDown className="w-4 h-4" />
                        <span className="font-bold">-25</span>
                      </div>
                      <p className="text-zinc-500 text-[10px]">mg/dL gesenkt</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {glucoseTab === 'Übersicht' && (
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <h3 className="text-white font-medium mb-3">{t('fitness.weekly_overview')}</h3>
                <div className="space-y-2">
                  {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((day, i) => (
                    <div key={day} className="flex items-center gap-3">
                      <span className="text-zinc-500 text-xs w-6">{day}</span>
                      <div className="flex-1 h-2 bg-zinc-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-green-500 rounded-full"
                          style={{ width: `${60 + Math.random() * 35}%` }}
                        />
                      </div>
                      <span className="text-zinc-400 text-xs">{Math.round(85 + Math.random() * 20)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {glucoseTab === 'Geräte' && (
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <h3 className="text-white font-medium mb-3">{t('fitness.connected_devices')}</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 bg-zinc-700/30 rounded-lg p-3">
                    <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                      <Droplet className="w-5 h-5 text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-white text-sm">Dexcom G7</p>
                      <p className="text-green-400 text-xs">Verbunden</p>
                    </div>
                  </div>
                  <button className="w-full py-3 border border-dashed border-zinc-700 rounded-lg text-zinc-500 text-sm">
                    {t('fitness.new_device')}
                  </button>
                </div>
              </div>
            )}

            {glucoseTab === 'Einstellungen' && (
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <h3 className="text-white font-medium mb-3">{t('fitness.target_settings')}</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-zinc-400 text-xs">{t('fitness.lower_limit')}</label>
                    <input 
                      type="number" 
                      defaultValue={70}
                      className="w-full mt-1 bg-zinc-700 text-white rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-xs">{t('fitness.upper_limit')}</label>
                    <input 
                      type="number" 
                      defaultValue={140}
                      className="w-full mt-1 bg-zinc-700 text-white rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              </div>
            )}

            {(glucoseTab === 'Mahlzeiten' || glucoseTab === 'Training') && (
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <h3 className="text-white font-medium mb-3">
                  {glucoseTab === 'Mahlzeiten' ? 'Mahlzeiten-Korrelation' : 'Training-Korrelation'}
                </h3>
                <p className="text-zinc-500 text-sm">
                  Hier werden {glucoseTab === 'Mahlzeiten' ? 'Mahlzeiten' : 'Trainings'} und deren Auswirkung auf den Blutzucker angezeigt.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FitnessScreen;
